import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import {
  Certificate, Plus, DownloadSimple, Seal, PaintBrush, CheckCircle,
  MagnifyingGlass, Eye,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PdfPreviewModal from "../components/PdfPreviewModal";

export default function CertificatesPage() {
  const { user } = useAuth();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [students, setStudents] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  // Multi-student generation — Round 57.
  const [form, setForm] = useState({ student_ids: [], course_id: "", programme_id: "", type: "course" });
  const [creating, setCreating] = useState(false);
  const [activeDesign, setActiveDesign] = useState(null);
  // Roster search / filter.
  const [certQuery, setCertQuery] = useState("");
  const [certProgFilter, setCertProgFilter] = useState("all");
  const [certStatusFilter, setCertStatusFilter] = useState("all");
  // Student picker search (inside the Generate dialog).
  const [studentQuery, setStudentQuery] = useState("");
  const [studentProgFilter, setStudentProgFilter] = useState("all");
  // Preview modal state.
  const [preview, setPreview] = useState(null);   // { url, title, filename } or null

  const isAdmin = ["super_admin", "admin", "instructor"].includes(user?.role);
  const canDesign = ["super_admin"].includes(user?.role);

  useEffect(() => {
    api.get("/certificates").then(r => { setCerts(r.data); setLoading(false); }).catch(() => setLoading(false));
    if (isAdmin) {
      api.get("/courses").then(r => setCourses(r.data)).catch(() => {});
      api.get("/programmes").then(r => setProgrammes(r.data)).catch(() => {});
      api.get("/students/eligible").then(r => {
        const seen = new Set();
        const list = [];
        for (const s of (r.data || [])) {
          if (seen.has(s.id)) continue;
          seen.add(s.id);
          list.push(s);
        }
        setStudents(list);
      }).catch(() => {});
      api.get("/designs/certificate/active").then(r => setActiveDesign(r.data)).catch(() => {});
    }
  }, [isAdmin]);

  const refreshCerts = () => api.get("/certificates").then(r => setCerts(r.data)).catch(() => {});

  // Filter the student picker down to the search/filter narrows.
  const filteredStudents = useMemo(() => {
    const q = studentQuery.trim().toLowerCase();
    return students
      .filter(s => studentProgFilter === "all" || s.programme_id === studentProgFilter)
      .filter(s => {
        if (!q) return true;
        return (s.name || "").toLowerCase().includes(q)
          || (s.email || "").toLowerCase().includes(q)
          || (s.admission_number || "").toLowerCase().includes(q)
          || (s.id || "").toLowerCase().includes(q);
      });
  }, [students, studentQuery, studentProgFilter]);

  const toggleStudent = (id) => {
    setForm(f => f.student_ids.includes(id)
      ? { ...f, student_ids: f.student_ids.filter(x => x !== id) }
      : { ...f, student_ids: [...f.student_ids, id] });
  };
  const selectAllVisible = () => setForm(f => ({ ...f, student_ids: filteredStudents.map(s => s.id) }));
  const clearSelection = () => setForm(f => ({ ...f, student_ids: [] }));

  /**
   * Generate certificates for every selected student. If the backend rejects
   * one for incomplete coursework (HTTP 403), collect those and offer the
   * admin a single "Generate anyway?" confirm — the retry passes
   * `admin_override: true` so the backend skips the completion checks.
   */
  const generateCerts = async () => {
    if (form.student_ids.length === 0) { toast.error("Select at least one student"); return; }
    if (form.type === "course" && !form.course_id) { toast.error("Select a course"); return; }
    if (form.type === "programme" && !form.programme_id) { toast.error("Select a programme"); return; }
    setCreating(true);
    const successes = [];
    const blocked = [];   // {student_id, name, reason}
    const failures = [];  // {student_id, name, reason}
    const payload = (sid, override = false) => ({
      student_id: sid,
      type: form.type,
      course_id: form.course_id,
      programme_id: form.programme_id,
      admin_override: override,
    });
    for (const sid of form.student_ids) {
      const student = students.find(s => s.id === sid);
      const name = student?.name || student?.email || sid;
      try {
        await api.post("/certificates/generate", payload(sid, false));
        successes.push(name);
      } catch (e) {
        const status = e.response?.status;
        const detail = e.response?.data?.detail || "Failed";
        if (status === 403 && /Certificate blocked|completion step/i.test(String(detail))) {
          blocked.push({ sid, name, reason: detail });
        } else {
          failures.push({ sid, name, reason: detail });
        }
      }
    }
    if (blocked.length > 0) {
      const names = blocked.map(b => `• ${b.name}`).join("\n");
      const ok = window.confirm(
        `${blocked.length} student${blocked.length === 1 ? "" : "s"} have NOT completed all requirements:\n\n${names}\n\nDo you want to generate their certificates anyway?`
      );
      if (ok) {
        for (const b of blocked) {
          try {
            await api.post("/certificates/generate", payload(b.sid, true));
            successes.push(`${b.name} (override)`);
          } catch (e) {
            failures.push({ sid: b.sid, name: b.name, reason: e.response?.data?.detail || "Failed" });
          }
        }
      } else {
        // Admin declined — those blocked students are treated as skipped (not failures).
      }
    }
    if (successes.length > 0) toast.success(`Generated ${successes.length} certificate${successes.length === 1 ? "" : "s"}.`);
    if (failures.length > 0) toast.error(`${failures.length} failed: ${failures.map(f => f.name).join(", ")}`);
    if (successes.length === 0 && failures.length === 0 && blocked.length > 0) {
      toast.info("No certificates generated.");
    }
    setCreateOpen(false);
    setForm({ student_ids: [], course_id: "", programme_id: "", type: "course" });
    setStudentQuery("");
    setStudentProgFilter("all");
    await refreshCerts();
    setCreating(false);
  };

  /**
   * Fetch a certificate PDF as a Blob and open the shared preview modal.
   * The same blob URL is also used by the modal's Download link so the
   * admin can save the exact PDF they're previewing without another
   * round-trip to the server.
   */
  const openPreview = async (cert) => {
    try {
      const { data } = await api.get(`/certificates/${cert.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      setPreview({
        url,
        title: `${cert.student_name} · ${cert.title} · ${cert.cert_number}`,
        filename: `${cert.cert_number || `certificate-${cert.id}`}.pdf`,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load certificate");
    }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  useEffect(() => {
    // Esc closes preview.
    if (!preview) return;
    const onKey = (e) => { if (e.key === "Escape") closePreview(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [preview]);   // eslint-disable-line react-hooks/exhaustive-deps

  const downloadPdf = async (cert) => {
    try {
      const { data } = await api.get(`/certificates/${cert.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${cert.cert_number || `certificate-${cert.id}`}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download certificate");
    }
  };

  const selectedCount = form.student_ids.length;

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="certificates-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1">Academic Records</p>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-950" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Certificates
            </h1>
          </div>
          {isAdmin && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="generate-cert-btn">
                  <Plus size={16} weight="bold" /> Generate Certificate
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl rounded-none max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Generate Certificate</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 mt-2">
                  <div>
                    <Label className="text-sm">Certificate Type</Label>
                    <div className="flex gap-1 mt-1 bg-slate-100 p-1">
                      {["course", "programme"].map(t => (
                        <button key={t} onClick={() => setForm({ ...form, type: t, course_id: "", programme_id: "" })}
                          className={`flex-1 py-2 text-xs font-medium transition-colors ${form.type === t ? "bg-blue-800 text-white" : "text-slate-600 hover:bg-slate-200"}`}>
                          {t === "course" ? "Course" : "Programme"}
                        </button>
                      ))}
                    </div>
                  </div>

                  {form.type === "course" && (
                    <div>
                      <Label className="text-sm">Course</Label>
                      <Select value={form.course_id} onValueChange={v => setForm({ ...form, course_id: v })}>
                        <SelectTrigger className="mt-1 rounded-none" data-testid="cert-course-select"><SelectValue placeholder="Select course" /></SelectTrigger>
                        <SelectContent>
                          {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {form.type === "programme" && (
                    <div>
                      <Label className="text-sm">Programme</Label>
                      <Select value={form.programme_id} onValueChange={v => setForm({ ...form, programme_id: v })}>
                        <SelectTrigger className="mt-1 rounded-none" data-testid="cert-prog-select"><SelectValue placeholder="Select programme" /></SelectTrigger>
                        <SelectContent>
                          {programmes.map(p => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label className="text-sm">Students (select one or more)</Label>
                      <div className="flex gap-2 text-[10px]">
                        <button type="button" onClick={selectAllVisible} className="text-blue-800 hover:underline" data-testid="select-all-students-btn">
                          Select all visible
                        </button>
                        <span className="text-slate-300">|</span>
                        <button type="button" onClick={clearSelection} className="text-slate-600 hover:underline" data-testid="clear-students-btn">
                          Clear
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1 mb-2">
                      <div className="relative">
                        <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          value={studentQuery}
                          onChange={e => setStudentQuery(e.target.value)}
                          placeholder="Search name/email/admission…"
                          className="w-full pl-6 pr-2 py-2 text-xs border border-slate-300 focus:outline-none focus:ring-1 focus:ring-blue-800"
                          data-testid="cert-student-search"
                        />
                      </div>
                      <Select value={studentProgFilter} onValueChange={setStudentProgFilter}>
                        <SelectTrigger className="rounded-none text-xs" data-testid="cert-student-prog-filter">
                          <SelectValue placeholder="Filter by programme" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All programmes (no filter)</SelectItem>
                          {programmes.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.title}` : p.title}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="border border-slate-200 max-h-64 overflow-y-auto" data-testid="cert-student-list">
                      {filteredStudents.length === 0 && (
                        <p className="p-3 text-xs text-slate-400 text-center">No matching eligible students.</p>
                      )}
                      {filteredStudents.map(s => {
                        const checked = form.student_ids.includes(s.id);
                        return (
                          <label
                            key={s.id}
                            className={`flex items-center gap-2 px-3 py-2 border-b border-slate-100 last:border-b-0 cursor-pointer hover:bg-slate-50 ${checked ? "bg-blue-50" : ""}`}
                            data-testid={`cert-student-row-${s.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleStudent(s.id)}
                              className="w-4 h-4"
                              data-testid={`cert-student-checkbox-${s.id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-slate-900 truncate">{s.name || s.email}</p>
                              <p className="text-[10px] text-slate-500 truncate">
                                {s.admission_number ? `${s.admission_number} · ` : ""}
                                {s.email}
                                {s.programme_code ? ` · ${s.programme_code}` : ""}
                              </p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">
                      {selectedCount > 0
                        ? `${selectedCount} student${selectedCount === 1 ? "" : "s"} selected. Only strict-enrolled students appear (approved · active · paid).`
                        : "Only strict-enrolled students appear (approved · active · paid). Tick the students you want to certify."}
                    </p>
                  </div>

                  <Button onClick={generateCerts} disabled={creating || selectedCount === 0} className="w-full bg-blue-800 text-white hover:bg-blue-900 rounded-none gap-2" data-testid="submit-cert-btn">
                    <Seal size={16} /> {creating ? "Generating…" : `Generate ${selectedCount > 0 ? `${selectedCount} ` : ""}Certificate${selectedCount === 1 ? "" : "s"}`}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {isAdmin && (
          <Card className="rounded-none border-amber-200 bg-amber-50/50 mb-6" data-testid="active-cert-template-card">
            <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div className="bg-amber-400 text-slate-950 w-10 h-10 flex items-center justify-center"><PaintBrush size={20} weight="bold" /></div>
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Active Certificate Template</p>
                  {activeDesign?.is_default ? (
                    <p className="text-sm font-semibold text-slate-900 truncate" data-testid="active-cert-template-name">Default (built-in) — no custom design saved yet</p>
                  ) : (
                    <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2" data-testid="active-cert-template-name">
                      <CheckCircle size={14} weight="fill" className="text-emerald-600" />
                      {activeDesign?.name || "Active design"}
                      {activeDesign?.version && <Badge className="rounded-none text-[10px] bg-blue-100 text-blue-800">v{activeDesign.version}</Badge>}
                    </p>
                  )}
                  <p className="text-xs text-slate-600 mt-0.5">All PDFs generated below use this design automatically.</p>
                </div>
              </div>
              {canDesign && (
                <Link to="/admin/designers/certificate">
                  <Button variant="outline" className="rounded-none border-amber-400 text-amber-800 hover:bg-amber-100 gap-2 text-xs" data-testid="open-cert-designer-btn">
                    <PaintBrush size={14} /> Edit / Apply Template
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className="h-36 bg-slate-100 animate-pulse" />)}
          </div>
        ) : certs.length === 0 ? (
          <div className="text-center py-16">
            <Certificate size={48} className="text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">{isAdmin ? "No certificates issued yet" : "You don't have any certificates yet"}</p>
          </div>
        ) : (
          <>
            {isAdmin && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4" data-testid="cert-filters">
                <div className="relative">
                  <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    value={certQuery}
                    onChange={e => setCertQuery(e.target.value)}
                    placeholder="Search certificate # · student · title"
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 focus:outline-none focus:ring-1 focus:ring-blue-800"
                    data-testid="cert-search"
                  />
                </div>
                <Select value={certProgFilter} onValueChange={setCertProgFilter}>
                  <SelectTrigger className="rounded-none" data-testid="cert-prog-filter">
                    <SelectValue placeholder="All programmes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All programmes</SelectItem>
                    {programmes.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.code ? `${p.code} · ${p.title}` : p.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={certStatusFilter} onValueChange={setCertStatusFilter}>
                  <SelectTrigger className="rounded-none" data-testid="cert-type-filter">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="course">Course</SelectItem>
                    <SelectItem value="programme">Programme</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {(() => {
              const q = certQuery.trim().toLowerCase();
              const filtered = certs.filter(c => {
                if (certProgFilter !== "all" && c.programme_id !== certProgFilter) return false;
                if (certStatusFilter !== "all" && (c.type || "").toLowerCase() !== certStatusFilter) return false;
                if (!q) return true;
                return (c.title || "").toLowerCase().includes(q)
                  || (c.student_name || "").toLowerCase().includes(q)
                  || (c.student_email || "").toLowerCase().includes(q)
                  || (c.cert_number || "").toLowerCase().includes(q);
              });
              if (filtered.length === 0) {
                return (
                  <div className="text-center py-10 text-xs text-slate-400 border border-dashed border-slate-200" data-testid="cert-empty-filtered">
                    No certificates match your search / filter.
                  </div>
                );
              }
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filtered.map(cert => (
                    <Card key={cert.id} className="border border-slate-200 rounded-none shadow-sm" data-testid={`cert-card-${cert.id}`}>
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <Seal size={28} className="text-amber-500" />
                          <Badge className="rounded-none text-[10px] bg-blue-100 text-blue-800">{cert.type.toUpperCase()}</Badge>
                        </div>
                        <h3 className="text-base font-semibold text-slate-900 mb-1 truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>{cert.title}</h3>
                        <p className="text-sm text-slate-600 mb-1">{cert.student_name}</p>
                        <p className="text-xs text-slate-400 mb-3">{cert.cert_number} | {cert.issued_date}</p>
                        <Separator className="mb-3" />
                        <div className="flex gap-2">
                          <Button
                            onClick={() => openPreview(cert)}
                            size="sm" variant="outline"
                            className="rounded-none gap-1 text-xs flex-1 border-blue-300 text-blue-800 hover:bg-blue-50"
                            data-testid={`preview-cert-${cert.id}`}
                          ><Eye size={14} /> Preview</Button>
                          <Button
                            onClick={() => downloadPdf(cert)}
                            size="sm" variant="outline"
                            className="rounded-none gap-1 text-xs flex-1"
                            data-testid={`download-cert-${cert.id}`}
                          ><DownloadSimple size={14} /> Download</Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              );
            })()}
          </>
        )}

        <PdfPreviewModal
          open={!!preview}
          url={preview?.url}
          title={preview?.title}
          filename={preview?.filename}
          onClose={closePreview}
        />
      </div>
    </Layout>
  );
}
